import { Suspense, useEffect, useMemo, useState } from 'react'

import { useGlobalState } from '../state/v2/store'
import { } from '../features/insights_hub/components/GlobalSearch/GlobalSearch'
import { LoadingScreen } from '../features/insights_hub/components/LoadingScreen/LoadingScreen'
import {
  FilterBar,
} from '../features/insights_hub/components/FilterBar/FilterBar'
import {
  KpiDisplay,
  yyyymmdd
} from '../display'
import { useInsightsFilterMenuParams } from '../features/insights_hub/components/FilterMenu/useFilterMenuParams'
import { DateRangePickerValue, getValidatedDateRange } from '../features/insights_hub/components/FilterBar/DateRangeFilter/DateRangeFilter'
import InsightsTable from '../features/insights_hub/components/InsightsTable/InsightsTable'
import { SelectAllMultiSelect } from '../components/Select/SelectAllMultiSelect'
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { ViewList } from '@mui/icons-material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { VisualInsights } from '../features/insights_hub/components/VisualInsights/VisualInsights'
import { TextInsights } from '../features/insights_hub/components/TextInsights/TextInsights'
import { AdditionalRecs } from '../features/insights_hub/components/AdditionalRecs/AdditionalRecs'
import { formatEnumString } from '../common'
import { CookieType, cookies } from '../state/cookies'
import { useTrackingContext } from '../analytics/useTracking'
import {
  trackInsightViewed,
  trackKpiChanged,
  trackDateRangeSelected,
  trackLabelSelected,
  trackViewSwitched,
} from '../analytics/tracking'
import { parse } from 'date-fns'
import { AISummary } from '../features/insights_hub/components/AISummary/AISummary'
import { LabelTypeLiftGraph } from '../features/insights_hub/components/LabelTypeLift/LabelTypeLiftGraph'
import { PrimaryColorTreemap } from '../features/insights_hub/components/PrimaryColorTreemap/PrimaryColorTreemap'
import { ExpandableCard } from '../features/insights_hub/components/ExpandableCard/ExpandableCard'
import { KPI } from '../features/insights_hub/components/TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'
interface InsightsProps {
  tagsEnabled: boolean,
  hookTextEnabled: boolean,
  headlineEnabled: boolean,
  dominantColorLabelTypeEnabled?: boolean,
  hasCurrentMonthData: boolean,
  insightsGraphEnabled: boolean,
  metricInsightGraphEnabled?: boolean,
  labelTypeLiftGraphEnabled?: boolean,
  containersGraphEnabled?: boolean
  primaryColorTreemapEnabled?: boolean
  treemapAdPreviewEnabled?: boolean
}
export const aiDateRanges:{label: string; value: string;}[] = [
  {
    label: "Last Month",
    value: "LM"
  },
  {
    label: "Last Quarter",
    value: "L3M"
  },
  {
    label: "Last Year",
    value: "L12M"
  },
  {
    label: "Cummulative",
    value: "CU"
  },
];

export const Insights = ({ tagsEnabled, hookTextEnabled, headlineEnabled, dominantColorLabelTypeEnabled = false, hasCurrentMonthData, insightsGraphEnabled, metricInsightGraphEnabled = false, labelTypeLiftGraphEnabled, containersGraphEnabled, primaryColorTreemapEnabled = false, treemapAdPreviewEnabled = false }: InsightsProps) => {
  let labelTypeOptions = [
    'IMAGE',
    'VIDEO',
    ...(dominantColorLabelTypeEnabled ? (['DOMINANT_COLOR'] as const) : []),
    'NOUN',
    'PHRASE',
    'TAG',
    'VERB',
  ] as string[];
  if (hookTextEnabled) {
    labelTypeOptions.push("HOOK_TEXT")
  }
  if (headlineEnabled) {
    labelTypeOptions.push("HEADLINE")
  }
  const brands = useGlobalState((s) => s.brand)
  const ctx = useTrackingContext()

  const [searchParams, setSearchParams] = useInsightsFilterMenuParams()
  const [view, setView] = useState(cookies.get(CookieType.INSIGHTS_VIEW) || 'table')
  const [expandedAISummary, setExpandedAISummary] = useState(false)
  const [expandedLabelTypeLift, setExpandedLabelTypeLift] = useState(false)
  const [expandedPrimaryColorTreemap, setExpandedPrimaryColorTreemap] = useState(false)
  const handleViewChange = (event: React.ChangeEvent<{}>, newView: string) => {
    if (newView !== null) {
      setView(newView)
      cookies.set(CookieType.INSIGHTS_VIEW, newView)
      window.dispatchEvent(new Event('insights-view-change'))
      trackViewSwitched({ brand_id: brands?.[0]?.id ?? '', account_id: ctx.accountId, view: newView === 'grid' ? 'visual' : 'list' }, ctx)
    }
  };
  const validLabelTypes = searchParams.labelType.filter(
    (labelType) => labelType != null && labelTypeOptions.includes(labelType)
  );
  
  useEffect(() => {
    if (validLabelTypes.length > 0) {
      setSearchParams({ ...searchParams, labelType: validLabelTypes });
    } else {
      const { labelType, ...otherParams } = searchParams;
      setSearchParams(otherParams);
    }
  }, [validLabelTypes]);
  
  
  const defaultSelectedFilters = useMemo(() => {
    if (searchParams.labelType.length > 0) {
      return searchParams.labelType.filter((labelType) => !!labelType).map((labelType) => labelType?.toUpperCase() || '')
    }
    return ['VIDEO', 'IMAGE', 'PHRASE', 'TAG']
  }, [searchParams.labelType])

  const kpiFilterOptions = useMemo(() => {
    if (!brands) return []

    return Array.from(new Set(brands.flatMap((b) => b.availableKpis))).filter((kpi) =>{
      return !(["PURCHASES", "PURCHASES_CONVERSION", "SPEND", "WEBSITE_PURCHASES", "TRIALS_STARTED", "IMPRESSIONS", "REACH"] as KPI[]).includes(kpi);
    }).map((kpi) => ({
      label: KpiDisplay[kpi],
      value: kpi,
    }))
  }, [brands])
  let selectedKpiOption = kpiFilterOptions.find(
    (opt) => opt.value === searchParams.insightKpi
  )

  const aiSelectedOption = aiDateRanges.find(
    (opt) => opt.value === searchParams.aiDateRange
  ) || aiDateRanges[0]
  
  useEffect(() => {
    if (!selectedKpiOption && kpiFilterOptions.length > 0) {
        setSearchParams({ insightKpi: kpiFilterOptions[0].value }, 'pushIn');
    }
}, [kpiFilterOptions, selectedKpiOption, setSearchParams]);

  const { insightKpi, performanceDateGte, performanceDateLte, aiDateRange } = searchParams;
  const [key, setKey] = useState(0);

  const getAISummaryCardClass = () => {
    if (primaryColorTreemapEnabled) return expandedAISummary ? 'col-span-2' : 'col-span-1'
    return expandedAISummary
      ? 'col-span-2'
      : expandedLabelTypeLift
      ? 'row-start-2 col-start-1 col-span-1'
      : 'col-span-1'
  }

  const getLabelTypeLiftCardClass = () => {
    if (primaryColorTreemapEnabled) return expandedLabelTypeLift ? 'col-span-2' : 'col-span-1'
    return expandedLabelTypeLift
      ? 'col-span-2 row-start-1'
      : expandedAISummary
      ? 'row-start-2 col-start-2 col-span-1'
      : 'col-span-1'
  }

  const selectedDateRange: DateRangePickerValue = getValidatedDateRange(
    searchParams.performanceDateGte,
    searchParams.performanceDateLte,
    hasCurrentMonthData
  );

  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [insightKpi, performanceDateGte, performanceDateLte, aiDateRange]);

  const brandId = brands?.[0]?.id ?? ''
  useEffect(() => {
    if (!insightKpi || !brandId) return
    trackInsightViewed({ brand_id: brandId, account_id: ctx.accountId, kpi: insightKpi }, ctx)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightKpi, brandId])

  if (!brands) {
    return (
      <div className="p-6 text-center">
        It looks like you aren't associated with any brands! Please reach out to
        customer success in order to get access.
      </div>
    )
  }

  return (
    <>
      <FilterBar>
        <FilterBar.FilterItemWrapper>
          <FilterBar.ItemLabel>Target KPI</FilterBar.ItemLabel>
          <FilterBar.SelectFilter
            name="kpi"
            options={kpiFilterOptions}
            getOptionValue={(opt) => opt.value}
            renderOptionLabel={(opt) => opt.label}
            value={selectedKpiOption}
            handleChange={(opt) => {
              if (opt?.value && opt.value !== insightKpi) {
                trackKpiChanged({ brand_id: brandId, account_id: ctx.accountId, kpi: opt.value, previous_kpi: insightKpi ?? undefined }, ctx)
              }
              setSearchParams({ insightKpi: opt?.value }, 'pushIn')
            }}
          />
        </FilterBar.FilterItemWrapper>

        {tagsEnabled && view === 'table' ? <FilterBar.FilterItemWrapper>
          <FilterBar.ItemLabel>Insight Dates</FilterBar.ItemLabel>
          <FilterBar.DateRangeFilter
            name="insightDates"
            value={selectedDateRange}
            handleChange={(range) => {
              const performanceDateGte = range[0] ? yyyymmdd(range[0]) : null
              const performanceDateLte = range[1] ? yyyymmdd(range[1]) : null
              if (performanceDateGte && performanceDateLte) {
                trackDateRangeSelected({ brand_id: brandId, account_id: ctx.accountId, kpi: insightKpi ?? '', surface: 'insights' }, ctx)
              }
              setSearchParams(
                {
                  performanceDateGte,
                  performanceDateLte,
                },
                'pushIn'
              )
            }}
            hasCurrentMonthData={hasCurrentMonthData}
          />
        </FilterBar.FilterItemWrapper>: (
          <FilterBar.FilterItemWrapper>
            <FilterBar.ItemLabel>AI Insight Date Ranges</FilterBar.ItemLabel>
            <FilterBar.SelectFilter
              name="dateRanges"
              options={aiDateRanges}
              getOptionValue={(opt) => opt.value}
              renderOptionLabel={(opt) => opt.label}
              value={aiSelectedOption}
              handleChange={(opt) =>
                setSearchParams({ aiDateRange: opt?.value }, 'pushIn')
              }
            />
          </FilterBar.FilterItemWrapper>
        )}
        {tagsEnabled && view === 'table' && <FilterBar.FilterItemWrapper>
          <FilterBar.ItemLabel>Label Types</FilterBar.ItemLabel>
          <SelectAllMultiSelect fieldLabel='' onChange={(labelType) => {
            const prev = (searchParams.labelType || []).filter((t): t is string => t != null)
            const added = labelType.filter((t) => !prev.includes(t))
            const removed = prev.filter((t) => !labelType.includes(t as string))
            ;[...added.map((t) => ({ t, sel: true })), ...removed.map((t) => ({ t, sel: false }))].forEach(({ t, sel }) => {
              trackLabelSelected({ brand_id: brandId, account_id: ctx.accountId, kpi: insightKpi ?? '', label_type: t as any, is_selected: sel }, ctx)
            })
            if (labelType.length === 0) {
              setSearchParams({ ...searchParams, labelType: ["THEME"] });
            } else {
              setSearchParams({ ...searchParams, labelType });
            }
          }} options={labelTypeOptions}
            defaultSelected={defaultSelectedFilters} getOptionValue={(option) => option.toUpperCase()}
            getOptionLabel={formatEnumString} />
        </FilterBar.FilterItemWrapper>}
        {tagsEnabled && <div className="ml-auto flex items-baseline py-2" >
          <ToggleButtonGroup value={view} exclusive onChange={handleViewChange}>
            <ToggleButton value="grid"><AutoAwesomeIcon /></ToggleButton>
            <ToggleButton value="table"><ViewList /></ToggleButton>
          </ToggleButtonGroup>
        </div>}
      </FilterBar>
      <div className="px-8 py-12">

        {view === 'table' && tagsEnabled ? <Suspense fallback={<LoadingScreen />}><InsightsTable insightsGraphEnabled={insightsGraphEnabled} metricInsightGraphEnabled={metricInsightGraphEnabled} /></Suspense>
          :
          <div className="flex flex-col gap-24 pt-4">
            {containersGraphEnabled === true ? (
              <div className="px-8 grid grid-cols-2 gap-4">
                <Suspense fallback={<LoadingScreen />}>
                  <div
                    className={getAISummaryCardClass()}
                  >
                    <ExpandableCard
                      title="AI Summary"
                      isExpanded={expandedAISummary}
                      onExpand={() => setExpandedAISummary(true)}
                      onCollapse={() => setExpandedAISummary(false)}
                    >
                      <AISummary evaluationType="TOP_PERFORMING" />
                    </ExpandableCard>
                  </div>
                </Suspense>
                {labelTypeLiftGraphEnabled && (
                  <Suspense fallback={<LoadingScreen />}>
                    <div
                      className={getLabelTypeLiftCardClass()}
                    >
                      <ExpandableCard
                        title="Label Type"
                        isExpanded={expandedLabelTypeLift}
                        onExpand={() => setExpandedLabelTypeLift(true)}
                        onCollapse={() => setExpandedLabelTypeLift(false)}
                      >
                        <LabelTypeLiftGraph />
                      </ExpandableCard>
                    </div>
                  </Suspense>
                )}
                {primaryColorTreemapEnabled && (
                  <Suspense fallback={<LoadingScreen />}>
                    <div className={expandedPrimaryColorTreemap ? 'col-span-2' : 'col-span-1'}>
                      <ExpandableCard
                        title="Primary Color Treemap"
                        isExpanded={expandedPrimaryColorTreemap}
                        onExpand={() => setExpandedPrimaryColorTreemap(true)}
                        onCollapse={() => setExpandedPrimaryColorTreemap(false)}
                      >
                        <PrimaryColorTreemap adPreviewEnabled={treemapAdPreviewEnabled} />
                      </ExpandableCard>
                    </div>
                  </Suspense>
                )}
              </div>
            ) : (
              <>
                <div className="px-8">
                  <Suspense fallback={<LoadingScreen />}>
                    <div className="w-full">
                      <AISummary evaluationType="TOP_PERFORMING" />
                    </div>
                  </Suspense>
                </div>
                {labelTypeLiftGraphEnabled && (
                  <div className="px-8">
                    <Suspense fallback={<LoadingScreen />}>
                      <LabelTypeLiftGraph />
                    </Suspense>
                  </div>
                )}
                {primaryColorTreemapEnabled && (
                  <div className="px-8">
                    <Suspense fallback={<LoadingScreen />}>
                      <PrimaryColorTreemap adPreviewEnabled={treemapAdPreviewEnabled} />
                    </Suspense>
                  </div>
                )}
              </>
            )}
          </div>
        }
      </div>
    </>
  )
}
